import React, { useState, useRef, useEffect } from 'react';
import { Book, ChevronRight, Plus, ChevronDown } from 'lucide-react';
import './Notebooks.css';

const DEFAULT_NOTEBOOKS = [
  {
    id: 'general',
    name: 'General',
    notes: []
  },
  {
    id: 'work',
    name: 'Work Notes',
    notes: []
  }
];

const Notebooks = () => {
  const [notebooks, setNotebooks] = useState(DEFAULT_NOTEBOOKS);
  const [currentNotebook, setCurrentNotebook] = useState(notebooks[0]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [isNotebookMenuOpen, setIsNotebookMenuOpen] = useState(false);
  const [isCreatingNotebook, setIsCreatingNotebook] = useState(false);
  const [newNotebookName, setNewNotebookName] = useState('');
  const titleRef = useRef(null);
  const contentRef = useRef(null);
  const notebookMenuRef = useRef(null);
  const newNotebookInputRef = useRef(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Close notebook menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notebookMenuRef.current && !notebookMenuRef.current.contains(event.target)) {
        setIsNotebookMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-focus title only on initial note selection
  useEffect(() => {
    if (selectedNote && titleRef.current && isInitialLoad) {
      titleRef.current.focus();
      setIsInitialLoad(false);
    }
  }, [selectedNote, isInitialLoad]);

  // Reset initial load state when switching notes
  useEffect(() => {
    setIsInitialLoad(true);
  }, [selectedNote?.id]);

  // Focus new notebook input when creating
  useEffect(() => {
    if (isCreatingNotebook && newNotebookInputRef.current) {
      newNotebookInputRef.current.focus();
    }
  }, [isCreatingNotebook]);

  const handleTitleChange = (e) => {
    if (!selectedNote) return;
    
    const newTitle = e.target.innerText;
    const updatedNote = { ...selectedNote, title: newTitle };
    setSelectedNote(updatedNote);
    
    // Update the note in the current notebook
    const updatedNotes = currentNotebook.notes.map(note => 
      note.id === selectedNote.id ? updatedNote : note
    );
    
    setCurrentNotebook(prev => ({
      ...prev,
      notes: updatedNotes
    }));
  };

  const handleContentChange = (e) => {
    if (!selectedNote) return;
    
    const newContent = e.target.innerText;
    const updatedNote = { ...selectedNote, content: newContent };
    setSelectedNote(updatedNote);
    
    // Update the note in the current notebook
    const updatedNotes = currentNotebook.notes.map(note => 
      note.id === selectedNote.id ? updatedNote : note
    );
    
    setCurrentNotebook(prev => ({
      ...prev,
      notes: updatedNotes
    }));
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      contentRef.current?.focus();
    }
  };

  const handleNewNotebookClick = (e) => {
    e.stopPropagation();
    setIsCreatingNotebook(true);
  };

  const handleNewNotebookKeyDown = (e) => {
    if (e.key === 'Enter' && newNotebookName.trim()) {
      createNewNotebook();
    } else if (e.key === 'Escape') {
      setIsCreatingNotebook(false);
      setNewNotebookName('');
    }
  };

  const createNewNote = () => {
    const newNote = {
      id: Date.now().toString(),
      title: '',
      content: '',
      createdAt: new Date().toISOString()
    };

    const updatedNotebook = {
      ...currentNotebook,
      notes: [newNote, ...currentNotebook.notes]
    };

    setCurrentNotebook(updatedNotebook);
    setSelectedNote(newNote);
    setIsInitialLoad(true);
  };

  const switchNotebook = (notebook) => {
    setCurrentNotebook(notebook);
    setSelectedNote(notebook.notes[0] || null);
    setIsNotebookMenuOpen(false);
  };

  const createNewNotebook = () => {
    if (!newNotebookName.trim()) return;

    const newNotebook = {
      id: Date.now().toString(),
      name: newNotebookName.trim(),
      notes: []
    };
    setNotebooks(prev => [...prev, newNotebook]);
    switchNotebook(newNotebook);
    setIsCreatingNotebook(false);
    setNewNotebookName('');
  };

  return (
    <div className="notebooks-container">
      <div className="notebooks-header">
        <div className="header-left">
          <div 
            className="notebook-selector" 
            onClick={() => setIsNotebookMenuOpen(!isNotebookMenuOpen)}
          >
            <div className="notebook-info">
              <Book size={18} />
              <h2>{currentNotebook.name}</h2>
              <ChevronDown size={16} className="chevron-icon" />
            </div>
            {isNotebookMenuOpen && (
              <div className="notebook-menu" ref={notebookMenuRef}>
                {notebooks.map(notebook => (
                  <button
                    key={notebook.id}
                    className={`notebook-item ${notebook.id === currentNotebook.id ? 'active' : ''}`}
                    onClick={() => switchNotebook(notebook)}
                  >
                    <Book size={16} />
                    {notebook.name}
                  </button>
                ))}
                <div className="menu-divider" />
                {isCreatingNotebook ? (
                  <div className="new-notebook-input" onClick={e => e.stopPropagation()}>
                    <input
                      ref={newNotebookInputRef}
                      type="text"
                      value={newNotebookName}
                      onChange={e => setNewNotebookName(e.target.value)}
                      onKeyDown={handleNewNotebookKeyDown}
                      placeholder="Notebook name..."
                    />
                  </div>
                ) : (
                  <button className="notebook-item new" onClick={handleNewNotebookClick}>
                    <Plus size={16} />
                    New Notebook
                  </button>
                )}
              </div>
            )}
          </div>
          {selectedNote && (
            <>
              <ChevronRight size={16} className="chevron-icon" />
              <div className="note-title">
                {selectedNote.title || 'Untitled'}
              </div>
            </>
          )}
        </div>
        <div className="header-right">
          <button className="new-note-btn" onClick={createNewNote}>
            <Plus size={16} />
            New Note
          </button>
          {selectedNote && (
            <div className="note-date">
              Created {new Date(selectedNote.createdAt).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>
      <div className="notebooks-content">
        {selectedNote ? (
          <div className="note-editor">
            <div
              ref={titleRef}
              className="note-title-editor"
              contentEditable
              onInput={handleTitleChange}
              onKeyDown={handleTitleKeyDown}
              data-placeholder="Title"
              suppressContentEditableWarning
            />
            <div
              ref={contentRef}
              className="note-content-editor"
              contentEditable
              onInput={handleContentChange}
              data-placeholder="Start writing..."
              suppressContentEditableWarning
            />
          </div>
        ) : (
          <div className="empty-state">
            <Book size={48} />
            <h3>No notes yet</h3>
            <p>Create a new note to get started</p>
            <button className="new-note-btn" onClick={createNewNote}>
              <Plus size={16} />
              New Note
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notebooks; 